import { useNavigate } from 'react-router-dom'
import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PaytrieAPI } from '@paytrie/client'

import { Button } from '@/components/Button/Button'
import {
  ErrorBlockText,
  LeftAlignSmallText,
} from '@/components/widget-components'
import { useMaintenanceStatus } from '@/hooks/useMaintenanceStatus'
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepFailed,
  trackOnboardingStepSubmitted,
  trackOnboardingStepViewed,
} from '@/utils/analytics'

// POTENTIAL IMPROVEMENT: Convert disclosure items to a configurable list for easier maintenance
// POTENTIAL IMPROVEMENT: Add checkbox confirmations for each disclosure item for better compliance
// POTENTIAL IMPROVEMENT: Include accessibility improvements for screen readers
export default function SignupDisclosure() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMaintenanceActive } = useMaintenanceStatus('pep-tpd-reconciliation')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const handleContinue = useCallback(
    async (e?: React.FormEvent<HTMLFormElement>) => {
      if (e) e.preventDefault()
      if (isSubmitting) return
      if (isMaintenanceActive) return

      trackOnboardingStepSubmitted('risk_disclosure')
      setIsSubmitting(true)
      setErrorMessage('')
      try {
        const res = await PaytrieAPI.updateCurrentUser({
          pep: false,
          tpd: false,
          currentFlow: PaytrieAPI.UpdateCurrentUserBodyCurrentFlow.DISCLOSURE,
        })
        if (res.status !== 200) {
          setErrorMessage(t('disclosure.error.unableToProcess'))
          trackOnboardingStepFailed('risk_disclosure', 'unable_to_process')
          return
        }
      } catch {
        setErrorMessage(t('disclosure.error.unableToProcess'))
        trackOnboardingStepFailed('risk_disclosure', 'unable_to_process')
        return
      } finally {
        setIsSubmitting(false)
      }

      trackOnboardingStepCompleted('risk_disclosure')
      navigate('/SignupUserInfo')
    },
    [isMaintenanceActive, isSubmitting, navigate, t]
  )

  useEffect(() => {
    trackOnboardingStepViewed('risk_disclosure')
  }, [])

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleContinue()
      }
    }

    document.addEventListener('keydown', handleKeyPress)
    return () => {
      document.removeEventListener('keydown', handleKeyPress)
    }
  }, [handleContinue])

  return (
    <form
      onSubmit={handleContinue}
      className="flex flex-col gap-5"
      data-testid="container-disclosure"
    >
      <div className="text-lg font-semibold uppercase">
        {t('disclosure.title')}
      </div>
      <div className="text-base">{t('disclosure.subtitle')}</div>

      <LeftAlignSmallText
        className="mt-1 sm:mt-1 md:mt-4 lg:mt-6"
        style={{ paddingBottom: '1em' }}
        data-testid="text-list-container"
      >
        <ol className="list-decimal space-y-3 ml-3">
          <li data-testid="text-item-1">{t('disclosure.item.1')}</li>
          <li data-testid="text-item-2">
            {t('disclosure.item.2.part1')}
            <a
              href={import.meta.env.VITE_PAYTRIE_EDU_LINK_URL}
              target="_blank"
              style={{ textDecoration: 'underline' }}
              data-testid="link-educational-resources"
            >
              {t('disclosure.item.2.link')}
            </a>
            {t('disclosure.item.2.part2')}
          </li>
          <li data-testid="text-item-3">{t('disclosure.item.3')}</li>
          <li data-testid="text-item-4">{t('disclosure.item.4')}</li>
        </ol>
      </LeftAlignSmallText>
      <div className="mb-4">
        <LeftAlignSmallText style={{ paddingBottom: '0em' }}>
          <p className="font-semibold" data-testid="text-acknowledgment">
            {t('disclosure.acknowledgment')}
          </p>
        </LeftAlignSmallText>
      </div>

      <ErrorBlockText
        data-testid={
          isMaintenanceActive
            ? 'text-disclosure-maintenance'
            : 'error-disclosure-message'
        }
      >
        {isMaintenanceActive ? t('disclosure.error.maintenance') : errorMessage}
      </ErrorBlockText>
      <Button
        type="submit"
        className="inline-flex items-center py-4 px-4 w-full justify-center lg:gap-2"
        disabled={isSubmitting || isMaintenanceActive}
        data-testid="btn-continue-disclosure"
      >
        {t('disclosure.submit')}
      </Button>
    </form>
  )
}
